import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CalendarClock, ChevronRight, Clock, Radio } from 'lucide-react'

import { formatRelative } from '@/lib/dates'

/**
 * Live + upcoming appointment surface on the Vehicle / Customer detail
 * pages. Renders either:
 *
 *   - a `kind="live"` tile (current time falls within an appointment) —
 *     orange-tinted, "Live now" pill, click → /appointments?appt=ID
 *     which auto-opens the edit dialog there (and Emil can dictate
 *     voice notes from inside that dialog once Task #4 lands).
 *   - a `kind="upcoming"` tile (starts within 24h) — softer brand blue,
 *     "Upcoming" pill with a relative-time label.
 *
 * Render-time formatting only: the route returns ISO timestamps; this
 * component decides how to display "Ends 11:30 AM" / "Starts in 45
 * minutes". Customer + vehicle labels are passed through verbatim so
 * the same tile works on both detail pages — the parent supplies
 * whichever side it has.
 */

export type LiveAppointmentTileEntry = {
  id: string
  starts_at: string
  ends_at: string
  status: string
  notes: string | null
  // Either customer-side or vehicle-side context. The detail page that
  // hosts the tile fills the OPPOSITE label so the tile shows the user
  // the thing they don't already know from the page title.
  customer_label?: string | null
  vehicle_label?: string | null
}

export function LiveAppointmentTile({
  appointment,
  kind,
}: {
  appointment: LiveAppointmentTileEntry
  kind: 'live' | 'upcoming'
}) {
  const { t } = useTranslation('appointments')

  const ends = new Date(appointment.ends_at)
  const starts = new Date(appointment.starts_at)
  const endsAtLabel = ends.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })

  const isLive = kind === 'live'
  // §4.10 schedule-block family: booked work is a system-held commitment,
  // so the GROUND may be saved-soft. The pill is a §4.9 lifecycle chip —
  // lifecycle states stay neutral (green fills are reserved for
  // saved-and-undoable); the icon + label carry live vs upcoming.
  const accent = isLive
    ? 'border-saved-line bg-saved-soft'
    : 'border-border bg-muted/40'
  const pillAccent = isLive
    ? 'border border-border bg-background text-foreground'
    : 'border border-border bg-background text-muted-foreground'

  const subLabel =
    appointment.vehicle_label ||
    appointment.customer_label ||
    appointment.notes ||
    null

  return (
    <Link
      to={`/appointments?appt=${encodeURIComponent(appointment.id)}`}
      className={`group block rounded-[10px] border p-4 transition-transform [transition-duration:120ms] active:scale-[0.99] motion-reduce:transition-none motion-reduce:active:scale-100 ${accent}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-[9px] py-[3px] text-[12px] font-[650] ${pillAccent}`}
            >
              {isLive ? (
                <Radio className="size-3" />
              ) : (
                <Clock className="size-3" />
              )}
              {isLive ? t('live_tile.live_now') : t('live_tile.upcoming')}
            </span>
            <span className="text-xs text-muted-foreground">
              {isLive
                ? t('live_tile.ends_at', { time: endsAtLabel })
                : t('live_tile.starts_in', {
                    relative: formatRelative(starts.toISOString()),
                  })}
            </span>
          </div>
          {subLabel ? (
            <div className="mt-1.5 truncate text-sm font-medium text-foreground">
              {subLabel}
            </div>
          ) : null}
          {appointment.notes && subLabel !== appointment.notes ? (
            <div className="mt-0.5 truncate text-xs text-muted-foreground">
              {appointment.notes}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:group-hover:text-foreground">
          <CalendarClock className="size-4" />
          <span className="hidden sm:inline">
            {t('live_tile.open_in_calendar')}
          </span>
          <ChevronRight className="size-4" />
        </div>
      </div>
    </Link>
  )
}
